<script setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { initPortalStoreAsync, usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNotify } from '@/composables/notify'
import { pxToRem } from '@/lib/utils'

import AvatarImage from '@/components/shared/AvatarImage.vue'

const AFTERNOON_START_HOUR = 12
const EVENING_START_HOUR = 18
const LOCAL_TLDS = Object.freeze(['localhost', 'internal'])
const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/
const WAVEFORM_BAR_COUNT = 200
const SHORT_LANDSCAPE_MAX_HEIGHT_REM = 30
const HERO_FEATURES = Object.freeze([
  { icon: 'mdi-chart-timeline-variant', labelKey: 'login.featurePlanning' },
  { icon: 'mdi-file-document-multiple-outline', labelKey: 'login.featureBudgeting' },
  { icon: 'mdi-movie-open-outline', labelKey: 'login.featureScheduling' },
])

const i18n = getI18n()
const $q = useQuasar()
const { notifyError } = useNotify()

await initPortalStoreAsync()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { rememberedLogin } = storeToRefs(webSiteStore)
await portalStore.setLanguageAsync(rememberedLogin.value?.language ?? portalStore.getBrowserLanguage())

const username = ref(rememberedLogin.value?.username ?? '')
const password = ref('')
const rememberDevice = ref(true)
const isLoggingIn = ref(false)
const isPasswordVisible = ref(false)
const isUsernameEdited = ref(false)

const getTimeGreetingKey = (hour) => {
  if (hour < AFTERNOON_START_HOUR) return 'login.goodMorning'
  if (hour < EVENING_START_HOUR) return 'login.goodAfternoon'
  return 'login.goodEvening'
}

const getWorkspaceName = (hostname) => {
  if (hostname.includes(':') || IPV4_PATTERN.test(hostname)) return null
  const labels = hostname.split('.')
  const domainLabelCount = LOCAL_TLDS.includes(labels.at(-1)) ? 1 : 2
  if (labels.length <= domainLabelCount) return null
  const [subdomain] = labels
  return subdomain.charAt(0).toUpperCase() + subdomain.slice(1)
}

const timeGreetingKey = getTimeGreetingKey(new Date().getHours())
const workspaceName = getWorkspaceName(window.location.hostname)

const isShortLandscape = computed(() => $q.screen.width > $q.screen.height
  && pxToRem($q.screen.height) <= SHORT_LANDSCAPE_MAX_HEIGHT_REM)

const isUserRecognized = computed(() => !isUsernameEdited.value
  && !!(rememberedLogin.value?.avatar || rememberedLogin.value?.initials))

const rememberedName = computed(() => rememberedLogin.value?.firstName || rememberedLogin.value?.username)

const greeting = computed(() => {
  const timeGreeting = i18n.t(timeGreetingKey)
  return rememberedName.value
    ? i18n.t('login.greetingWithName', { greeting: timeGreeting, name: rememberedName.value })
    : timeGreeting
})

const signInLine = computed(() => (!rememberedLogin.value && workspaceName)
  ? { keypath: 'login.signInToWorkspace', name: workspaceName }
  : { keypath: 'login.signInTo', name: rememberedLogin.value?.companyName ?? i18n.t('common.appName') })

const onUsernameUpdated = (value) => {
  username.value = value
  isUsernameEdited.value = true
}

const loginAsync = async () => {
  isLoggingIn.value = true
  try {
    await webSiteStore.loginAsync(username.value, password.value, rememberDevice.value)
  } catch (error) {
    notifyError(i18n.t('error.invalidCredentials'))
    console.error(error)
  }
  isLoggingIn.value = false
}

const forgetDevice = async () => {
  webSiteStore.forgetRememberedLogin()
  username.value = ''
  await portalStore.setLanguageAsync(portalStore.getBrowserLanguage())
}

const isRequired = val => !!val && !!val.trim() || i18n.t('common.required')

const isLoginButtonEnabled = () => {
  return username.value && password.value && !isLoggingIn.value && username.value.trim() && password.value.trim()
}

</script>

<template>
  <q-layout view="hHh Lpr lFf">
    <q-page-container>
      <q-page :class="['login-page row no-wrap', { 'login-page--landscape': isShortLandscape }]">
        <div class="film-strip film-strip--top" />
        <div class="glow glow--primary" />
        <div class="glow glow--secondary" />
        <div class="spotlight lt-md" aria-hidden="true" />
        <div class="waveform row items-end justify-center no-wrap" aria-hidden="true">
          <span
            v-for="bar in WAVEFORM_BAR_COUNT"
            :key="bar"
            class="waveform-bar"
            :style="{ '--bar-index': bar }"
          />
        </div>

        <section class="brand-panel gt-sm column">
          <div class="brand-header gt-sm row items-center no-wrap">
            <img src="/img/logo-white.svg" :alt="$t('common.appName')" class="brand-logo q-mr-sm">
            <span class="brand-name">{{ $t('common.appName') }}</span>
          </div>

          <transition appear name="slide-fade">
            <div class="brand-hero gt-sm">
              <h1 class="hero-title">
                {{ $t('login.heroTitle') }}
              </h1>
              <p class="hero-subtitle">
                {{ $t('login.heroSubtitle') }}
              </p>
              <ul class="hero-features">
                <li
                  v-for="(feature, index) in HERO_FEATURES"
                  :key="feature.labelKey"
                  class="hero-feature row items-center no-wrap"
                  :style="{ '--feature-index': index }"
                >
                  <span class="hero-feature-icon flex flex-center q-mr-md">
                    <q-icon :name="feature.icon" size="sm" />
                  </span>
                  {{ $t(feature.labelKey) }}
                </li>
              </ul>
            </div>
          </transition>

        </section>
        <div class="film-strip film-strip--bottom" />

        <section class="form-panel column justify-center items-center q-pa-md">
          <transition appear name="slide-fade">
            <div class="login-container relative-position column items-center">
              <q-card flat class="login-card full-width">
                <q-card-section :class="['login-identity column items-center', isShortLandscape ? 'q-pa-md' : 'q-pt-lg q-pb-none']">
                  <img
                    v-if="rememberedLogin?.logo"
                    :src="rememberedLogin.logo"
                    :alt="rememberedLogin.companyName"
                    :class="['tenant-logo', { 'tenant-logo--compact': isUserRecognized }]"
                  >
                  <img
                    v-else-if="!isUserRecognized"
                    src="/img/logo.svg"
                    :alt="$t('common.appName')"
                    class="app-logo"
                  >
                  <div v-if="isUserRecognized" class="user-avatar-ring q-mt-md">
                    <avatar-image
                      :size="isShortLandscape ? '4rem' : '6rem'"
                      :image="rememberedLogin.avatar ?? ''"
                      :initials="rememberedLogin.initials ?? ''"
                    />
                  </div>
                  <div class="greeting text-center q-mt-md">
                    {{ greeting }}
                  </div>
                  <i18n-t
                    :keypath="signInLine.keypath"
                    tag="div"
                    class="text-body2 text-grey-7 text-center q-mt-xs"
                  >
                    <template #name>
                      <b class="sign-in-name">{{ signInLine.name }}</b>
                    </template>
                  </i18n-t>
                </q-card-section>

                <q-card-section :class="['login-form', isShortLandscape ? 'q-pa-md' : 'q-px-lg q-pt-lg']">
                  <q-form autocomplete="off" :class="['column', isShortLandscape ? 'q-gutter-y-sm' : 'q-gutter-y-md']" @keydown.enter="loginAsync">
                    <div class="row q-col-gutter-sm">
                      <q-input
                        :model-value="username"
                        class="col-12"
                        autocomplete="username"
                        :dense="isShortLandscape"
                        :label="$t('common.username')"
                        :rules="[isRequired]"
                        @update:model-value="onUsernameUpdated"
                      />
                      <q-input
                        v-model="password"
                        class="col-12"
                        autocomplete="current-password"
                        :dense="isShortLandscape"
                        :type="isPasswordVisible ? 'text' : 'password'"
                        :label="$t('common.password')"
                        :rules="[isRequired]"
                      >
                        <template #append>
                          <q-icon :name="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'" class="cursor-pointer" @click="isPasswordVisible = !isPasswordVisible" />
                        </template>
                      </q-input>
                      <q-checkbox
                        v-model="rememberDevice"
                        class="col-12"
                        :label="$t('login.rememberDevice')"
                        :dense="isShortLandscape"
                      />
                    </div>
                    <q-btn
                      unelevated
                      no-caps
                      :size="isShortLandscape ? 'md' : 'lg'"
                      text-color="white"
                      class="login-btn full-width"
                      icon-right="mdi-arrow-right"
                      :disabled="!isLoginButtonEnabled()"
                      :loading="isLoggingIn"
                      :label="$t('common.login')"
                      @click="loginAsync"
                    />
                  </q-form>
                  <div v-if="rememberedLogin" class="text-caption text-grey-7 text-center q-mt-md">
                    {{ $t('login.notUser', { name: rememberedName }) }}
                    <q-btn
                      flat
                      dense
                      no-caps
                      padding="0"
                      color="primary"
                      class="text-caption text-weight-medium"
                      :label="$t('login.forgetDevice')"
                      @click="forgetDevice"
                    />
                  </div>
                </q-card-section>
              </q-card>

              <div v-if="rememberedLogin" class="powered-by row items-center justify-center no-wrap q-mt-md text-caption">
                <img src="/img/logo.svg" :alt="$t('common.appName')" class="powered-by-logo q-mr-xs">
                {{ $t('login.poweredBy', { name: $t('common.appName') }) }}
              </div>
            </div>
          </transition>
        </section>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<style scoped lang="scss">
$brand-gradient: linear-gradient(135deg, $primary, $secondary);
$page-gradient: linear-gradient(135deg, mix(black, $primary, 35%), mix(black, $secondary, 40%));
$mobile-form-lift: 3.5rem;

.login-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 15% 20%, rgba(white, 0.1), transparent 45%),
    radial-gradient(circle at 85% 85%, rgba(black, 0.2), transparent 45%),
    $page-gradient;
}

.brand-panel {
  position: relative;
  z-index: 1;
  flex: 1 1 55%;
  padding: 3.5rem 4rem 9rem;
  color: white;
}

.film-strip {
  position: absolute;
  left: 0;
  right: 0;
  height: 1.25rem;
  background:
    repeating-linear-gradient(90deg, rgba(white, 0.12) 0 1rem, transparent 1rem 2rem)
    0 50% / 2rem 0.5rem repeat-x,
    rgba(black, 0.35);
  animation: film-roll 4s linear infinite;

  &--top {
    top: 0;
  }

  &--bottom {
    bottom: 0;
  }
}

@keyframes film-roll {
  to {
    background-position: 2rem 50%, 0 0;
  }
}

.glow {
  position: absolute;
  width: 32rem;
  height: 32rem;
  border-radius: 50%;
  filter: blur(5rem);
  opacity: 0.45;
  animation: glow-drift 14s ease-in-out infinite alternate;

  &--primary {
    top: -10rem;
    right: -8rem;
    background: $primary;
  }

  &--secondary {
    bottom: -12rem;
    left: -6rem;
    background: $secondary;
    animation-delay: -7s;
  }
}

@keyframes glow-drift {
  to {
    transform: translate(-4rem, 3rem) scale(1.15);
  }
}

.brand-logo {
  width: 2.5rem;
  height: 2.5rem;
}

.brand-name {
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.brand-hero {
  max-width: 34rem;
  margin-block: auto;
}

.hero-title {
  margin: 0 0 1.25rem;
  font-size: clamp(2.5rem, 4vw, 3.75rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.02em;
  background: linear-gradient(90deg, white 30%, mix(white, $secondary, 45%));
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.hero-subtitle {
  margin: 0 0 2.5rem;
  font-size: 1.125rem;
  line-height: 1.6;
  color: rgba(white, 0.72);
}

.hero-features {
  margin: 0;
  padding: 0;
  list-style: none;
}

.hero-feature {
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgba(white, 0.9);
  opacity: 0;
  animation: feature-in 0.6s ease-out forwards;
  animation-delay: calc(0.6s + var(--feature-index) * 0.15s);
}

.hero-feature-icon {
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  background: rgba(white, 0.08);
  border: 1px solid rgba(white, 0.14);
  backdrop-filter: blur(0.5rem);
}

@keyframes feature-in {
  from {
    opacity: 0;
    transform: translateX(-1rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.waveform {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2.75rem;
  gap: 0.25rem;
  height: 4rem;
  overflow: hidden;
  opacity: 0.55;
}

.waveform-bar {
  flex: 0 0 0.375rem;
  height: 100%;
  border-radius: 1rem;
  background: linear-gradient(to top, rgba(white, 0.15), rgba(white, 0.6));
  transform-origin: bottom;
  animation: wave 1.6s ease-in-out infinite alternate;
  animation-delay: calc(var(--bar-index) * -0.17s);

  &:nth-child(3n) {
    animation-duration: 1.1s;
  }

  &:nth-child(4n + 1) {
    animation-duration: 2.1s;
  }
}

@keyframes wave {
  from {
    transform: scaleY(0.12);
  }
  to {
    transform: scaleY(1);
  }
}

.form-panel {
  position: relative;
  z-index: 1;
  flex: 1 1 35%;
  @include glass-surface(0.25, 1rem, 150%);
  box-shadow: inset 1px 0 0 rgba(white, 0.25);
}

.login-container {
  width: 100%;
  max-width: 25rem;
}

.login-card {
  border-radius: 1.25rem;
  background: white;
  border: 1px solid rgba(white, 0.6);
  box-shadow:
    inset 0 1px 0 rgba(white, 0.7),
    0 0 4rem rgba(white, 0.3),
    0 1.5rem 3rem -1rem rgba(black, 0.3);

  .body--dark & {
    background: $dark;
    border-color: rgba(white, 0.18);
    box-shadow:
      inset 0 1px 0 rgba(white, 0.12),
      0 1.5rem 3rem -1rem rgba(black, 0.5);
  }
}

.greeting {
  font-size: 1.625rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.sign-in-name {
  color: $primary;
}

.login-btn {
  background: $brand-gradient;
  background-size: 200% 100%;
  box-shadow: 0 0.5rem 1.5rem -0.5rem rgba($primary, 0.7);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-position 0.4s ease;

  &:not([disabled]):hover {
    transform: translateY(-0.125rem);
    background-position: 100% 0;
    box-shadow: 0 0.75rem 2rem -0.5rem rgba($secondary, 0.7);
  }

  &[disabled] {
    box-shadow: none;
  }
}

.app-logo {
  width: 4.5rem;
  height: 4.5rem;
  animation: logo-pulse 3s ease-in-out 1s;
}

@keyframes logo-pulse {
  0%,
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
  20% {
    transform: scale(1.1);
  }
  35% {
    transform: scale(1);
    filter: brightness(1);
  }
  42% {
    filter: brightness(1.8);
  }
  50% {
    filter: brightness(1);
  }
}

.user-avatar-ring {
  padding: 0.25rem;
  border-radius: 50%;
  background: $brand-gradient;
  box-shadow: 0 0.75rem 2rem -0.5rem rgba($primary, 0.6);
  animation: avatar-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s backwards;

  :deep(.q-avatar) {
    box-shadow: 0 0 0 0.2rem white;
    font-size: 2.25rem;
    font-weight: 600;
    color: white;
  }

  :deep(.q-avatar img) {
    object-fit: cover;
  }
}

@keyframes avatar-pop {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}

.tenant-logo {
  max-width: 100%;
  max-height: 3.5rem;
  object-fit: contain;

  &--compact {
    max-height: 2.25rem;
  }
}

.powered-by {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  color: rgba(white, 0.8);
}

.powered-by-logo {
  width: 1rem;
  height: 1rem;
}

.spotlight {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 46rem;
  height: 34rem;
  margin: -17rem 0 0 -23rem;
  border-radius: 50%;
  pointer-events: none;
  mix-blend-mode: screen;
  background: radial-gradient(closest-side, rgba(white, 0.6), rgba(mix(white, $primary, 75%), 0.3) 55%, transparent);
  animation: spotlight-flicker 6s ease-in-out infinite;
}

@keyframes spotlight-flicker {
  0%,
  100% {
    opacity: 1;
  }
  45% {
    opacity: 0.85;
  }
  55% {
    opacity: 0.95;
  }
}

@media (max-width: $breakpoint-sm-max) {
  .waveform {
    bottom: 2rem;
    height: 3rem;
  }

  .spotlight {
    top: calc(50% - #{$mobile-form-lift});
  }

  .form-panel {
    flex: 1 1 100%;
    padding-bottom: 2 * $mobile-form-lift;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: none;

    .body--dark & {
      background: transparent;
    }
  }
}

.login-page--landscape {
  .waveform,
  .spotlight {
    display: none;
  }

  .form-panel {
    justify-content: safe center;
    padding: 1.75rem max(1rem, env(safe-area-inset-right)) 1.75rem max(1rem, env(safe-area-inset-left));
  }

  .login-container {
    max-width: 34rem;
  }

  .login-card {
    display: flex;
    align-items: center;
  }

  .login-identity {
    flex: 1 1 auto;
    min-width: 0;
  }

  .login-form {
    flex: 0 0 18rem;
  }

  .app-logo {
    width: 3rem;
    height: 3rem;
  }

  .greeting {
    font-size: 1.25rem;
  }

  .powered-by {
    position: static;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-logo,
  .user-avatar-ring,
  .spotlight,
  .film-strip,
  .glow,
  .waveform-bar {
    animation: none;
  }

  .hero-feature {
    animation: none;
    opacity: 1;
  }
}

.slide-fade-enter-active {
  transition: all 1s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(1.875rem);
  opacity: 0;
}
</style>
